import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence } from 'framer-motion';
import { User, MapPin, Sprout, CheckCircle, ArrowRight, ArrowLeft, Edit, Award, Upload, X, FileText, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import ApiService from '../services/api';
import theme from '../theme';
import { logCustomEvent } from '../firebase';

// Custom StepConnector for elegant golden gradient lines
const CustomStepConnector = () => (
  <StepConnector
    sx={{
      '& .MuiStepConnector-line': {
        height: 2,
        border: 0,
        backgroundColor: 'rgba(212, 167, 71, 0.15)',
        borderRadius: 1,
        marginTop: '-1px',
        position: 'relative',
        zIndex: 1,
      },
      '&.Mui-active .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
      '&.Mui-completed .MuiStepConnector-line': {
        background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
      },
    }}
  />
);

const AwardsApplication = () => {
  const { t, i18n } = useTranslation('awards');
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Phone verification state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Questions state for farming profile
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionResponses, setQuestionResponses] = useState({});

  // Stepper scroll state
  const stepperRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Location autocomplete state
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [hoblis, setHoblis] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTaluks, setLoadingTaluks] = useState(false);
  const [loadingHoblis, setLoadingHoblis] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [selectedDistrictId, setSelectedDistrictId] = useState(null);
  const [selectedTalukId, setSelectedTalukId] = useState(null);
  const [selectedHobliId, setSelectedHobliId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedVillageId, setSelectedVillageId] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    // Region and Category Selection
    role: '',
    regionLevel: '',
    category: '',

    // Personal Details
    fullName: '',
    aadharNumber: '',
    email: '',
    phoneNumber: '',
    state: 'Karnataka',
    district: '',
    zone: '',
    taluk: '',
    hobli: '',
    village: '',

    // Farming Profile
    yearsOfExperience: '',
    domain: '',
    landUnderCultivation: '',
    cropDetails: '',

    // Achievements & Proof - Per Criterion
    criteriaResponses: [],

    // Supporting Documents for Farmers/FPO
    supportingDocuments: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [showValidationError, setShowValidationError] = useState(false);

  // Dynamic steps based on user role
  // Farmer/FPO: Combined category+phone → Personal Details with questions → Farming Profile → Review
  // Innovator: Category → Phone → Personal Details → Achievements → Review
  const getSteps = () => {
    if (formData.role === 'farmer' || formData.role === 'fpo') {
      return [
        t('stepper.categorySelection'),
        t('stepper.personalDetails'),
        t('stepper.farmingProfile'),
        t('stepper.documentUpload'),
        t('stepper.reviewSubmit')
      ];
    } else {
      // Innovator uses the original flow
      return [
        t('stepper.categorySelection'),
        t('stepper.phoneVerification'),
        t('stepper.personalDetails'),
        t('stepper.achievementsProof'),
        t('stepper.reviewSubmit')
      ];
    }
  };

  const steps = getSteps();

  // Helper function to get step name by index
  const getStepName = (stepIndex) => {
    return steps[stepIndex];
  };

  // Modern field styling matching Sponsor form
  const modernFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: 'rgba(212, 167, 71, 0.02)',
      border: '1px solid rgba(212, 167, 71, 0.15)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(212, 167, 71, 0.04)',
        borderColor: 'rgba(212, 167, 71, 0.3)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(212, 167, 71, 0.05)',
        borderColor: '#D4A747',
        boxShadow: '0 0 0 3px rgba(212, 167, 71, 0.1)',
        '& fieldset': { border: 'none' }
      },
      '&.Mui-error': {
        backgroundColor: 'rgba(244, 67, 54, 0.02)',
        borderColor: 'rgba(244, 67, 54, 0.3)',
        '&:hover': {
          backgroundColor: 'rgba(244, 67, 54, 0.04)',
          borderColor: 'rgba(244, 67, 54, 0.5)'
        },
        '&.Mui-focused': {
          backgroundColor: 'rgba(244, 67, 54, 0.05)',
          borderColor: '#f44336',
          boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.1)'
        }
      },
      '& fieldset': { border: 'none' }
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontWeight: 500,
      backgroundColor: 'transparent',
      '&.MuiInputLabel-shrink': {
        backgroundColor: 'white',
        padding: '0 6px',
        marginLeft: '-6px',
        borderRadius: '4px'
      },
      '&.Mui-focused': {
        color: '#D4A747'
      },
      '&.Mui-error': {
        color: '#f44336'
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
      marginLeft: 0,
      marginTop: '6px',
      fontWeight: 400
    }
  };

  const zoneMapping = {
    'Zone 1': ['Mysuru', 'Mandya', 'Ramanagara', 'Chamarajanagar', 'Hassan'],
    'Zone 2': ['Bengaluru Rural', 'Chikkaballapur', 'Kolar', 'Chitradurga', 'Tumakuru'],
    'Zone 3': ['Davanagere', 'Shivamogga', 'Chikkamagaluru', 'Ballari', 'Dakshina Kannada', 'Udupi'],
    'Zone 4': ['Dharwad', 'Belagavi', 'Gadag', 'Koppal', 'Haveri', 'Uttara Kannada'],
    'Zone 5': ['Vijayapura', 'Bagalkot', 'Kalaburagi', 'Bidar', 'Raichur', 'Yadgir']
  };

  // Get roles from translations
  const roles = [
    { value: 'farmer', label: t('categorySelection.role.farmer') },
    { value: 'fpo', label: t('categorySelection.role.fpo') },
    { value: 'innovation', label: t('categorySelection.role.innovator') }
  ];

  // Auto-detect zone based on district
  useEffect(() => {
    if (formData.district) {
      const zone = Object.keys(zoneMapping).find(z =>
        zoneMapping[z].includes(formData.district)
      );
      if (zone) {
        setFormData(prev => ({ ...prev, zone }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.district]);

  // Warn user before leaving page if form has data or submission is in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Check if user has started filling the form or is in the middle of submission
      const hasFormData = formData.role || formData.fullName || formData.phoneNumber ||
                          activeStep > 0 || otpVerified ||
                          (formData.supportingDocuments && formData.supportingDocuments.length > 0);

      // Always warn during submission or if form has data (and not yet successfully submitted)
      if ((loading || hasFormData) && !submitSuccess) {
        // Standard way to trigger browser's confirmation dialog
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData, activeStep, loading, submitSuccess, otpVerified]);

  // Fetch districts on component mount
  useEffect(() => {
    const fetchDistricts = async () => {
      setLoadingDistricts(true);
      try {
        const data = await ApiService.fetchPlaces('district', 2);
        setDistricts(data || []);
      } catch (err) {
        console.error('Failed to fetch districts:', err);
        setDistricts([]);
      } finally {
        setLoadingDistricts(false);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch taluks when district is selected
  useEffect(() => {
    if (selectedDistrictId) {
      const fetchTaluks = async () => {
        setLoadingTaluks(true);
        setTaluks([]);
        setHoblis([]);
        setVillages([]);
        setSelectedTalukId(null);
        setSelectedHobliId(null);
        setSelectedVillageId(null);
        setFormData(prev => ({ ...prev, taluk: '', hobli: '', village: '' }));
        try {
          const data = await ApiService.fetchPlaces('taluk', selectedDistrictId);
          setTaluks(data || []);
        } catch (err) {
          console.error('Failed to fetch taluks:', err);
          setTaluks([]);
        } finally {
          setLoadingTaluks(false);
        }
      };
      fetchTaluks();
    }
  }, [selectedDistrictId]);

  // Fetch hoblis when taluk is selected
  useEffect(() => {
    if (selectedTalukId) {
      const fetchHoblis = async () => {
        setLoadingHoblis(true);
        setHoblis([]);
        setVillages([]);
        setSelectedHobliId(null);
        setSelectedVillageId(null);
        setFormData(prev => ({ ...prev, hobli: '', village: '' }));
        try {
          const data = await ApiService.fetchPlaces('block', selectedTalukId);
          if (data && data.length > 0) {
            setHoblis(data);
          } else {
            // No hoblis found, fill both hobli and village with taluk name
            setHoblis([]);
            setFormData(prev => ({ ...prev, hobli: prev.taluk, village: prev.taluk }));
          }
        } catch (err) {
          console.error('Failed to fetch hoblis:', err);
          // On error, fill both hobli and village with taluk name
          setHoblis([]);
          setFormData(prev => ({ ...prev, hobli: prev.taluk, village: prev.taluk }));
        } finally {
          setLoadingHoblis(false);
        }
      };
      fetchHoblis();
    }
  }, [selectedTalukId]);

  // Fetch villages when hobli is selected
  useEffect(() => {
    if (selectedHobliId) {
      const fetchVillages = async () => {
        setLoadingVillages(true);
        setVillages([]);
        setSelectedVillageId(null);
        setFormData(prev => ({ ...prev, village: '' }));
        try {
          const data = await ApiService.fetchPlaces('village', selectedHobliId);
          if (data && data.length > 0) {
            setVillages(data);
          } else {
            // No villages found, fill village with taluk name
            setVillages([]);
            setFormData(prev => ({ ...prev, village: prev.taluk }));
          }
        } catch (err) {
          console.error('Failed to fetch villages:', err);
          // On error, fill village with taluk name
          setVillages([]);
          setFormData(prev => ({ ...prev, village: prev.taluk }));
        } finally {
          setLoadingVillages(false);
        }
      };
      fetchVillages();
    }
  }, [selectedHobliId]);

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const currentLang = i18n.language || 'en';
      console.log(`Fetching award categories for language: ${currentLang}`);
      const data = await ApiService.getCategories(currentLang);
      console.log(`Fetched ${data.length} categories for language: ${currentLang}`);
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Failed to load award categories. Please try again.');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch categories when component mounts or when language changes
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Fetch questions for farming profile based on selected category
  const fetchQuestions = async (categoryId, language = 'en') => {
    if (!categoryId) return;

    setLoadingQuestions(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const response = await fetch(`${apiUrl}/questions/category/${categoryId}?lan=${language}`);

      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }

      const data = await response.json();
      console.log('Fetched questions:', data);

      // Sort questions by order field in ascending order
      const sortedQuestions = (data.questions || data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setQuestions(sortedQuestions);

      // Initialize responses for each question
      const initialResponses = {};
      sortedQuestions.forEach(q => {
        initialResponses[q._id] = q.choiceType === 'multiple' ? [] : '';
      });
      setQuestionResponses(initialResponses);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Fetch questions when entering farming profile step (step 2 for farmers) or when language changes
  useEffect(() => {
    const currentLang = i18n.language || 'en';
    if ((formData.role === 'farmer' || formData.role === 'fpo') && formData.category && activeStep === 2 && !loadingQuestions) {
      console.log('Fetching questions for category:', formData.category, 'language:', currentLang);
      fetchQuestions(formData.category, currentLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category, formData.role, activeStep, i18n.language]);

  // Filter categories based on role and region level
  useEffect(() => {
    if (formData.role && formData.regionLevel) {
      console.log('Filtering categories by role:', formData.role, 'and regionLevel:', formData.regionLevel);

      // API returns category and awardType in English regardless of language
      // Only awardName and description are translated
      const filtered = categories.filter(cat => {
        const matchesRole = cat.category?.toLowerCase() === formData.role.toLowerCase();
        const matchesRegionLevel = cat.awardType?.toLowerCase() === formData.regionLevel.toLowerCase();
        return matchesRole && matchesRegionLevel && cat.isActive !== false;
      });

      console.log('Filtered categories:', filtered.length, 'categories found');
      setFilteredCategories(filtered);
    } else {
      console.log('Clearing filtered categories - role or regionLevel not selected');
      setFilteredCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role, formData.regionLevel, categories, i18n.language]);

  // Pre-fill form from URL query parameters (from Nominations page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    const category = params.get('category');
    const awardType = params.get('awardType');

    if (role || category || awardType) {
      console.log('Pre-filling form from URL params:', { role, category, awardType });

      setFormData(prev => ({
        ...prev,
        ...(role && { role }),
        ...(category && { category }),
        ...(awardType && { regionLevel: awardType })
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Initialize or update criteriaResponses when category or language changes
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const selectedCategory = categories.find(c => c._id === formData.category);
      if (selectedCategory && selectedCategory.qualificationCriteria && selectedCategory.qualificationCriteria.length > 0) {
        // Update criteriaResponses with new language criteria names while preserving user input
        const newResponses = selectedCategory.qualificationCriteria.map((criterion, index) => {
          // If we have existing response at this index, preserve the user's input
          const existingResponse = formData.criteriaResponses?.[index];
          return {
            criterionName: criterion,
            achievementDescription: existingResponse?.achievementDescription || '',
            documents: existingResponse?.documents || [],
            noDocumentsReason: existingResponse?.noDocumentsReason || ''
          };
        });

        setFormData(prev => ({
          ...prev,
          criteriaResponses: newResponses
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category, categories, i18n.language]);

  // Send OTP
  const handleSendOtp = async () => {
    // Validate phone number
    if (!phoneNumber || !/^[6-9]\d{9}$/.test(phoneNumber)) {
      setFormErrors({ phoneNumber: 'Please enter a valid 10-digit Indian mobile number' });
      return;
    }

    setSendingOtp(true);
    setError('');

    try {
      // Proceed to send OTP
      const response = await fetch('https://bhoomee.tedoratech.com/otp/sendOTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      setOtpSent(true);
      setFormErrors({});

      // Track OTP sent event
      logCustomEvent('otp_sent', {
        role: formData.role
      });
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');

      // Track OTP send failure
      logCustomEvent('otp_send_failed', {
        role: formData.role,
        error: err.message
      });
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setFormErrors({ otp: 'Please enter a valid 6-digit OTP' });
      return;
    }

    setVerifyingOtp(true);
    setError('');

    try {
      // Verify OTP and proceed without fetching user details
      const response = await fetch('https://bhoomee.tedoratech.com/otp/verifyOTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid OTP');
      }

      const result = await response.json();

      if (result.status === 'SUCCESS') {
        setOtpVerified(true);
        setFormErrors({});

        // Auto-populate phone number for innovators
        setFormData(prev => ({
          ...prev,
          phoneNumber: phoneNumber
        }));

        // Track OTP verification success
        logCustomEvent('otp_verified', {
          role: formData.role
        });

        // Auto-navigate to next step - use setTimeout to ensure state updates are processed
        // Farmers: step 1 (Personal Details) - combined flow
        // FPO/Innovator: step 2 (Personal Details) - separate phone verification at step 1
        setTimeout(() => {
          setActiveStep((formData.role === 'farmer' || formData.role === 'fpo') ? 1 : 2);
        }, 0);
      } else {
        throw new Error('OTP verification failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify OTP. Please try again.');

      // Track OTP verification failure
      logCustomEvent('otp_verification_failed', {
        role: formData.role,
        error: err.message
      });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setShowValidationError(false);
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Stepper scroll functionality
  const checkScrollArrows = () => {
    if (stepperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = stepperRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  const scrollStepper = (direction) => {
    if (stepperRef.current) {
      const scrollAmount = 200;
      stepperRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    checkScrollArrows();
    const handleResize = () => checkScrollArrows();
    window.addEventListener('resize', handleResize);

    const stepperElement = stepperRef.current;
    if (stepperElement) {
      stepperElement.addEventListener('scroll', checkScrollArrows);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (stepperElement) {
        stepperElement.removeEventListener('scroll', checkScrollArrows);
      }
    };
  }, [activeStep]);

  // Handlers for criteria responses
  const handleCriterionAchievementChange = (criterionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex ? { ...response, achievementDescription: value } : response
      )
    }));
    setShowValidationError(false);

    // Clear error
    const errorKey = `criterionAchievement_${criterionIndex}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleCriterionFileUpload = (criterionIndex, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newDocuments = files.map(file => ({
      file,
      reason: '',
      id: Date.now() + Math.random()
    }));

    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex
          ? { ...response, documents: [...(response.documents || []), ...newDocuments] }
          : response
      )
    }));
    setShowValidationError(false);
  };

  const handleCriterionRemoveDocument = (criterionIndex, docId) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex
          ? { ...response, documents: (response.documents || []).filter(doc => doc.id !== docId) }
          : response
      )
    }));
    setShowValidationError(false);
  };

  const handleCriterionNoDocReasonChange = (criterionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      criteriaResponses: prev.criteriaResponses.map((response, index) =>
        index === criterionIndex ? { ...response, noDocumentsReason: value } : response
      )
    }));
    setShowValidationError(false);

    // Clear error
    const errorKey = `criterionNoDocReason_${criterionIndex}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handlers for supporting documents (Farmers/FPO)
  const handleSupportingDocumentUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newDocuments = files.map((file, index) => ({
      file,
      id: Date.now() + Math.random() + index // Ensure unique IDs even for files selected at same time
    }));

    console.log(`Adding ${files.length} documents:`, files.map(f => f.name));

    setFormData(prev => {
      const updatedDocs = [...(prev.supportingDocuments || []), ...newDocuments];
      console.log(`Total documents after adding: ${updatedDocs.length}`);
      return {
        ...prev,
        supportingDocuments: updatedDocs
      };
    });
    setShowValidationError(false);

    // Reset file input to allow selecting the same files again
    event.target.value = '';

    // Clear document error if exists
    if (formErrors.supportingDocuments) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.supportingDocuments;
        return newErrors;
      });
    }
  };

  const handleRemoveSupportingDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      supportingDocuments: (prev.supportingDocuments || []).filter(doc => doc.id !== docId)
    }));
    setShowValidationError(false);
  };

  const validateStep = (step) => {
    const errors = {};
    const stepName = getStepName(step);

    switch (stepName) {
      case t('stepper.categorySelection'):
        if (!formData.role) errors.role = t('categorySelection.errors.role');
        if (!formData.category) errors.category = t('categorySelection.errors.category');
        if (!formData.regionLevel) errors.regionLevel = t('categorySelection.errors.regionLevel');
        // Only farmers have combined phone verification in category step
        if ((formData.role === 'farmer' || formData.role === 'fpo') && !otpVerified) {
          errors.phoneVerification = t('phoneVerification.errors.phoneVerification');
        }
        break;

      case t('stepper.phoneVerification'):
        // Only for Innovator (separate step)
        if (!otpVerified) {
          errors.phoneVerification = t('phoneVerification.errors.phoneVerification');
        }
        break;

      case t('stepper.personalDetails'):
        if (!formData.fullName) errors.fullName = t('personalDetails.errors.fullName');
        // Aadhar is optional, but validate format if provided
        if (formData.aadharNumber && !/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ''))) {
          errors.aadharNumber = t('personalDetails.errors.aadharNumber');
        }
        if (!formData.phoneNumber) {
          errors.phoneNumber = t('personalDetails.errors.phoneNumber');
        } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
          errors.phoneNumber = t('personalDetails.errors.phoneNumber');
        }
        if (!formData.district) errors.district = t('personalDetails.errors.district');
        if (!formData.taluk) errors.taluk = t('personalDetails.errors.taluk');
        if (!formData.hobli) errors.hobli = t('personalDetails.errors.hobli');
        if (!formData.village) errors.village = t('personalDetails.errors.village');
        break;

      case t('stepper.farmingProfile'):
        // Validate all questions - all must be answered
        questions.forEach(question => {
          const response = questionResponses[question._id];
          if (!response || (Array.isArray(response) && response.length === 0) || response === '') {
            errors[`question_${question._id}`] = 'This field is required';
          }
        });
        break;

      case t('stepper.documentUpload'):
        // Document upload is optional for farmers/FPO - no validation required
        // Documents are encouraged but not mandatory
        break;

      case t('stepper.achievementsProof'):
        // Only for Innovator (separate step)
        if (formData.criteriaResponses && formData.criteriaResponses.length > 0) {
          formData.criteriaResponses.forEach((response, criterionIndex) => {
            if (!response.achievementDescription || response.achievementDescription.trim() === '') {
              errors[`criterionAchievement_${criterionIndex}`] = t('achievementsProof.achievementDescription.error');
            } else if (response.achievementDescription.trim().length < 50) {
              errors[`criterionAchievement_${criterionIndex}`] = t('achievementsProof.achievementDescription.minLength');
            }
            if (!response.documents || response.documents.length === 0) {
              if (!response.noDocumentsReason || response.noDocumentsReason.trim() === '') {
                errors[`criterionNoDocReason_${criterionIndex}`] = t('achievementsProof.documents.noDocumentsReason.error');
              }
            }
          });
        }
        break;


      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setShowValidationError(false);
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Track step progression
      const stepNameMapping = {
        'Category Selection': 'category_selection',
        'Phone Verification': 'phone_verification',
        'Personal Details': 'personal_details',
        'Farming Profile': 'farming_profile',
        'Achievements & Proof': 'criteria_responses',
        'Review & Submit': 'review_submit'
      };
      const currentStepName = getStepName(activeStep);
      logCustomEvent('form_step_completed', {
        step: activeStep,
        stepName: stepNameMapping[currentStepName] || currentStepName.toLowerCase().replace(/\s+/g, '_'),
        nextStep: nextStep,
        role: formData.role
      });
    } else {
      setShowValidationError(true);
    }
  };

  const handleBack = () => {
    setShowValidationError(false);
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    // Validate all steps before submission (excluding review step)
    // Farmer/FPO: 4 steps (0: Category+Phone, 1: Personal+Questions, 2: Farming Profile, 3: Document Upload)
    // Innovator: 4 steps (0: Category, 1: Phone, 2: Personal, 3: Achievements)
    const stepsToValidate = (formData.role === 'farmer' || formData.role === 'fpo') ? [0, 1, 2, 3] : [0, 1, 2, 3];
    const allStepsValid = stepsToValidate.every(step => validateStep(step));
    if (!allStepsValid) {
      setError('Please complete all required fields in all sections');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

      // Build answers array from questionResponses
      const answers = questions.map(question => {
        const response = questionResponses[question._id];
        let answerIds = [];

        if (response) {
          if (Array.isArray(response)) {
            // Multiple choice - map optionNames to optionIds
            answerIds = response.map(selectedOptionName => {
              const option = question.options?.find(opt => opt.optionName === selectedOptionName);
              return option?.optionId || selectedOptionName;
            });
          } else {
            // Single choice or text - map optionName to optionId if it's a choice question
            if (question.choiceType === 'single' && question.options) {
              const option = question.options.find(opt => opt.optionName === response);
              answerIds = [option?.optionId || response];
            } else {
              // Text/number input - store as is
              answerIds = [response];
            }
          }
        }

        return {
          questionId: question.questionId,
          answerId: answerIds
        };
      }).filter(answer => answer.answerId.length > 0);

      // Step 1: Submit application data (without files)
      const applicationData = {
        role: formData.role,
        regionLevel: formData.regionLevel,
        category: formData.category,
        fullName: formData.fullName,
        aadharNumber: formData.aadharNumber || '',
        phoneNumber: formData.phoneNumber,
        state: formData.state,
        district: formData.district,
        taluk: formData.taluk,
        hobli: formData.hobli,
        village: formData.village,
        yearsOfExperience: formData.yearsOfExperience,
        domain: formData.domain,
        landUnderCultivation: formData.landUnderCultivation,
        cropDetails: formData.cropDetails,
        criteriaResponses: formData.criteriaResponses || [],
        answers: answers
      };

      console.log('Submitting application to backend...', applicationData);
      console.log(`Supporting documents count at submission: ${formData.supportingDocuments?.length || 0}`);
      if (formData.supportingDocuments?.length > 0) {
        console.log('Supporting documents:', formData.supportingDocuments.map(d => ({ name: d.file.name, size: d.file.size, id: d.id })));
      }

      // Create submission
      const response = await fetch(`${apiUrl}/submissions/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }

      const result = await response.json();
      console.log('Application submitted successfully:', result);

      const submissionId = result.submission.id;
      setApplicationId(result.submission.appId || submissionId);

      // Step 2: Upload documents from all criteria responses
      if (formData.criteriaResponses && formData.criteriaResponses.length > 0) {
        let totalDocuments = 0;
        let uploadedCount = 0;

        // Count total documents
        formData.criteriaResponses.forEach(response => {
          if (response.documents && response.documents.length > 0) {
            totalDocuments += response.documents.length;
          }
        });

        if (totalDocuments > 0) {
          console.log(`Uploading ${totalDocuments} documents...`);

          // Upload documents for each criterion
          for (let i = 0; i < formData.criteriaResponses.length; i++) {
            const criterionResponse = formData.criteriaResponses[i];

            if (criterionResponse.documents && criterionResponse.documents.length > 0) {
              for (let j = 0; j < criterionResponse.documents.length; j++) {
                const doc = criterionResponse.documents[j];
                const fileFormData = new FormData();
                fileFormData.append('files', doc.file);
                fileFormData.append('criterionName', criterionResponse.criterionName);

                uploadedCount++;
                console.log(`Uploading document ${uploadedCount}/${totalDocuments}: ${doc.file.name} for criterion: ${criterionResponse.criterionName}`);

                const uploadResponse = await fetch(`${apiUrl}/uploads/${submissionId}`, {
                  method: 'POST',
                  body: fileFormData,
                });

                if (!uploadResponse.ok) {
                  const uploadError = await uploadResponse.json();
                  console.error(`Failed to upload document ${uploadedCount}:`, uploadError);
                  // Continue with other uploads even if one fails
                } else {
                  const uploadResult = await uploadResponse.json();
                  console.log(`Document ${uploadedCount} uploaded:`, uploadResult);
                }
              }
            }
          }

          console.log('All documents uploaded successfully');
        }
      }

      // Step 3: Upload supporting documents for Farmers/FPO
      if ((formData.role === 'farmer' || formData.role === 'fpo') && formData.supportingDocuments && formData.supportingDocuments.length > 0) {
        const totalDocs = formData.supportingDocuments.length;
        console.log(`Uploading ${totalDocs} supporting documents...`);

        // Create upload promises for all documents
        const uploadPromises = formData.supportingDocuments.map(async (doc, index) => {
          const fileFormData = new FormData();
          fileFormData.append('files', doc.file);
          fileFormData.append('criterionName', 'Supporting Documents');

          console.log(`Starting upload of supporting document ${index + 1}/${totalDocs}: ${doc.file.name}`);

          try {
            const uploadResponse = await fetch(`${apiUrl}/uploads/${submissionId}`, {
              method: 'POST',
              body: fileFormData,
            });

            if (!uploadResponse.ok) {
              const uploadError = await uploadResponse.json();
              console.error(`Failed to upload supporting document ${index + 1}:`, uploadError);
              return { success: false, fileName: doc.file.name, error: uploadError };
            } else {
              const uploadResult = await uploadResponse.json();
              console.log(`Supporting document ${index + 1} uploaded successfully:`, uploadResult);
              return { success: true, fileName: doc.file.name, result: uploadResult };
            }
          } catch (uploadErr) {
            console.error(`Error uploading supporting document ${index + 1}:`, uploadErr);
            return { success: false, fileName: doc.file.name, error: uploadErr.message };
          }
        });

        // Wait for ALL uploads to complete
        const uploadResults = await Promise.all(uploadPromises);
        const successCount = uploadResults.filter(r => r.success).length;
        const failCount = uploadResults.filter(r => !r.success).length;

        console.log(`Supporting documents upload complete: ${successCount} succeeded, ${failCount} failed out of ${totalDocs}`);

        if (failCount > 0) {
          console.warn('Some documents failed to upload:', uploadResults.filter(r => !r.success));
        }
      }

      setSubmitSuccess(true);
      // Set to the step after the last step (success view)
      // Success step: Farmer/FPO has 5 steps (0-4), Innovator has 5 steps (0-4)
      setActiveStep(5);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Track successful form submission
      logCustomEvent('application_submitted', {
        role: formData.role,
        category: formData.category,
        regionLevel: formData.regionLevel,
        district: formData.district
      });
    } catch (err) {
      setError(err.message || 'Failed to submit application. Please try again.');
      console.error('Submission error:', err);

      // Track form submission failure
      logCustomEvent('application_submission_failed', {
        role: formData.role,
        category: formData.category,
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    const stepName = getStepName(step);

    switch (stepName) {
      case t('stepper.categorySelection'):
        const regionalLevelOptions = {
          farmer: [
            { value: 'district', label: t('categorySelection.regionLevel.district') },
            { value: 'zonal', label: t('categorySelection.regionLevel.zonal') }
          ],
          fpo: [
            { value: 'zonal', label: t('categorySelection.regionLevel.zonal') }
          ],
          innovation: [
            { value: 'state', label: t('categorySelection.regionLevel.state') }
          ]
        };

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <MapPin size={20} style={{ marginRight: 6, color: '#D4A747' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('categorySelection.sectionTitle')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* Role Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  select
                  label={t('categorySelection.role.label')}
                  value={formData.role}
                  onChange={(e) => {
                    handleInputChange('role', e.target.value);
                    // Reset category and regional level when role changes
                    handleInputChange('category', '');
                    handleInputChange('regionLevel', '');
                  }}
                  error={!!formErrors.role}
                  helperText={formErrors.role}
                  size="small"
                  sx={modernFieldStyle}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Regional Level Selection - only show when role is selected */}
              {formData.role && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    select
                    label={t('categorySelection.regionLevel.label')}
                    value={formData.regionLevel}
                    onChange={(e) => {
                      handleInputChange('regionLevel', e.target.value);
                      // Reset category when regional level changes
                      handleInputChange('category', '');
                    }}
                    error={!!formErrors.regionLevel}
                    helperText={formErrors.regionLevel}
                    size="small"
                    sx={modernFieldStyle}
                  >
                    {(regionalLevelOptions[formData.role] || []).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Award Categories as Cards - only show when role and regional level are selected */}
              {formData.role && formData.regionLevel && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ color: '#1A1A1A', fontWeight: 600 }}>
                      {t('categorySelection.category.label')}
                    </Typography>
                    {!loadingCategories && filteredCategories.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        ({filteredCategories.length} {filteredCategories.length === 1 ? t('categorySelection.category.category') : t('categorySelection.category.categories')} {t('categorySelection.category.categoriesAvailable')})
                      </Typography>
                    )}
                  </Box>

                  {loadingCategories ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <CircularProgress size={30} sx={{ color: '#D4A747' }} />
                      <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                        {t('categorySelection.category.loading')}
                      </Typography>
                    </Box>
                  ) : filteredCategories.length === 0 ? (
                    <Alert severity="warning">
                      {t('categorySelection.category.noCategories')}
                    </Alert>
                  ) : (
                    <>
                      <Grid container spacing={1} sx={{ alignItems: 'stretch' }}>
                        {filteredCategories.map((cat) => (
                          <Grid size={{ xs: 12, sm: 4 }} key={cat._id} sx={{ display: 'flex' }}>
                            <Paper
                              onClick={() => handleInputChange('category', cat._id)}
                              sx={{
                                p: 1.25,
                                cursor: 'pointer',
                                border: formData.category === cat._id
                                  ? '2px solid #D4A747'
                                  : '1px solid rgba(212, 167, 71, 0.15)',
                                borderRadius: '6px',
                                background: formData.category === cat._id
                                  ? 'linear-gradient(135deg, rgba(212, 167, 71, 0.08) 0%, rgba(255, 255, 255, 1) 100%)'
                                  : 'rgba(255, 255, 255, 1)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                '&:hover': {
                                  borderColor: '#D4A747',
                                  boxShadow: '0 2px 8px rgba(212, 167, 71, 0.12)',
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, color: '#B8901C', fontSize: '0.85rem', lineHeight: 1.2 }}>
                                  {cat.awardName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                  <Chip
                                    label={cat.awardType}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(212, 167, 71, 0.1)',
                                      color: '#B8901C',
                                      fontWeight: 600,
                                      fontSize: '0.6rem',
                                      height: 18
                                    }}
                                  />
                                  {formData.category === cat._id && (
                                    <CheckCircle size={16} style={{ color: '#D4A747', flexShrink: 0 }} />
                                  )}
                                </Box>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#666',
                                  lineHeight: 1.3,
                                  flex: 1
                                }}
                              >
                                {cat.description}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                  {formErrors.category && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {formErrors.category}
                    </Alert>
                  )}
                </Grid>
              )}

              {/* Phone Verification Section - For Farmers and FPO (combined flow) */}
              {(formData.role === 'farmer' || formData.role === 'fpo') && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Phone size={18} style={{ marginRight: 6, color: '#D4A747' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                        {t('phoneVerification.title')}
                      </Typography>
                    </Box>
                  </Grid>

                  {!otpVerified ? (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">
                      {t('phoneVerification.subtitle')}
                    </Alert>
                  </Grid>

                  {/* Phone Number Input */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('phoneVerification.phoneNumber.label')}
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(value);
                        setShowValidationError(false);
                        if (formErrors.phoneNumber) {
                          setFormErrors(prev => ({ ...prev, phoneNumber: '' }));
                        }
                      }}
                      error={!!formErrors.phoneNumber}
                      helperText={formErrors.phoneNumber}
                      placeholder={t('phoneVerification.phoneNumber.placeholder')}
                      disabled={otpSent}
                      size="small"
                      sx={modernFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1, color: '#666' }}>+91</Typography>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    {!otpSent ? (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSendOtp}
                        disabled={sendingOtp || !phoneNumber}
                        sx={{
                          background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                          color: 'white',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                          },
                          '&:disabled': {
                            background: 'rgba(212, 167, 71, 0.3)',
                          }
                        }}
                      >
                        {sendingOtp ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            {t('phoneVerification.buttons.sending')}
                          </>
                        ) : (
                          t('phoneVerification.buttons.verifyPhone')
                        )}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setFormErrors({});
                        }}
                        sx={{
                          borderColor: '#D4A747',
                          color: '#D4A747',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          '&:hover': {
                            borderColor: '#B8901C',
                            backgroundColor: 'rgba(212, 167, 71, 0.05)',
                          }
                        }}
                      >
                        {t('phoneVerification.buttons.changeNumber')}
                      </Button>
                    )}
                  </Grid>

                  {/* OTP Input */}
                  {otpSent && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="success">
                          {t('phoneVerification.messages.otpSent')}
                        </Alert>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          required
                          label={t('phoneVerification.otp.label')}
                          value={otp}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(value);
                            setShowValidationError(false);
                            if (formErrors.otp) {
                              setFormErrors(prev => ({ ...prev, otp: '' }));
                            }
                          }}
                          error={!!formErrors.otp}
                          helperText={formErrors.otp}
                          placeholder={t('phoneVerification.otp.placeholder')}
                          size="small"
                          sx={modernFieldStyle}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleVerifyOtp}
                          disabled={verifyingOtp || !otp}
                          sx={{
                            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                            color: 'white',
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                            },
                            '&:disabled': {
                              background: 'rgba(212, 167, 71, 0.3)',
                            }
                          }}
                        >
                          {verifyingOtp ? (
                            <>
                              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                              {t('phoneVerification.buttons.verifying')}
                            </>
                          ) : (
                            t('phoneVerification.buttons.verifyOtp')
                          )}
                        </Button>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                          <Button
                            size="small"
                            onClick={handleSendOtp}
                            disabled={sendingOtp}
                            sx={{
                              color: '#D4A747',
                              textTransform: 'none',
                              fontSize: '0.875rem',
                              '&:hover': {
                                backgroundColor: 'rgba(212, 167, 71, 0.05)',
                              }
                            }}
                          >
                            {t('phoneVerification.buttons.resendOtp')}
                          </Button>
                        </Box>
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">
                    <strong>Phone Verified Successfully!</strong>
                    <br />
                    You can now proceed to the next step.
                  </Alert>
                </Grid>
              )}

              {(formData.role === 'farmer' || formData.role === 'fpo') && formErrors.phoneVerification && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">
                    {formErrors.phoneVerification}
                  </Alert>
                </Grid>
              )}

              {(formData.role === 'farmer' || formData.role === 'fpo') && error && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">
                    {error}
                  </Alert>
                </Grid>
              )}
                </>
              )}
            </Grid>
          </motion.div>
        );

      case t('stepper.phoneVerification'):
        // Separate phone verification step for Innovator only
        // Get selected category for qualification criteria
        const selectedCategoryForPhone = categories.find(c => c._id === formData.category);

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('phoneVerification.title')}
              </Typography>
            </Box>

            {/* Qualification Criteria Section */}
            {selectedCategoryForPhone && selectedCategoryForPhone.qualificationCriteria && selectedCategoryForPhone.qualificationCriteria.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('phoneVerification.documentsReady', { defaultValue: 'Please keep the following documents ready before proceeding:' })}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {selectedCategoryForPhone.qualificationCriteria.map((criterion, index) => (
                      <li key={index}>
                        <Typography variant="body2">{criterion}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              </Box>
            )}

            {!otpVerified ? (
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    {t('phoneVerification.subtitle')}
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label={t('phoneVerification.phoneNumber.label')}
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhoneNumber(value);
                      setShowValidationError(false);
                      if (formErrors.phoneNumber) {
                        setFormErrors(prev => ({ ...prev, phoneNumber: '' }));
                      }
                    }}
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                    placeholder={t('phoneVerification.phoneNumber.placeholder')}
                    disabled={otpSent}
                    size="small"
                    sx={modernFieldStyle}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, color: '#666' }}>+91</Typography>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {!otpSent ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSendOtp}
                      disabled={sendingOtp || !phoneNumber}
                      sx={{
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                        color: 'white',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                        },
                        '&:disabled': {
                          background: 'rgba(212, 167, 71, 0.3)',
                        }
                      }}
                    >
                      {sendingOtp ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                          {t('phoneVerification.buttons.sending')}
                        </>
                      ) : (
                        t('phoneVerification.buttons.verifyPhone')
                      )}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                        setFormErrors({});
                      }}
                      sx={{
                        borderColor: '#D4A747',
                        color: '#D4A747',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: '#B8901C',
                          backgroundColor: 'rgba(212, 167, 71, 0.05)',
                        }
                      }}
                    >
                      {t('phoneVerification.buttons.changeNumber')}
                    </Button>
                  )}
                </Grid>

                {otpSent && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="success">
                        {t('phoneVerification.messages.otpSent')}
                      </Alert>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label={t('phoneVerification.otp.label')}
                        value={otp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(value);
                          setShowValidationError(false);
                          if (formErrors.otp) {
                            setFormErrors(prev => ({ ...prev, otp: '' }));
                          }
                        }}
                        error={!!formErrors.otp}
                        helperText={formErrors.otp}
                        placeholder={t('phoneVerification.otp.placeholder')}
                        size="small"
                        sx={modernFieldStyle}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleVerifyOtp}
                        disabled={verifyingOtp || !otp}
                        sx={{
                          background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                          color: 'white',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                          },
                          '&:disabled': {
                            background: 'rgba(212, 167, 71, 0.3)',
                          }
                        }}
                      >
                        {verifyingOtp ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            {t('phoneVerification.buttons.verifying')}
                          </>
                        ) : (
                          t('phoneVerification.buttons.verifyOtp')
                        )}
                      </Button>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Button
                          size="small"
                          onClick={handleSendOtp}
                          disabled={sendingOtp}
                          sx={{
                            color: '#D4A747',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              backgroundColor: 'rgba(212, 167, 71, 0.05)',
                            }
                          }}
                        >
                          {t('phoneVerification.buttons.resendOtp')}
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">
                    <strong>Phone Verified Successfully!</strong>
                    <br />
                    You can now proceed to the next step.
                  </Alert>
                </Grid>
              </Grid>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </motion.div>
        );

      case t('stepper.personalDetails'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <User size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('personalDetails.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 12 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.fullName')}
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={!!formErrors.fullName}
                  helperText={formErrors.fullName}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 6 }}>
                <TextField
                  fullWidth
                  label={t('personalDetails.fields.aadharNumber')}
                  value={formData.aadharNumber}
                  onChange={(e) => handleInputChange('aadharNumber', e.target.value)}
                  error={!!formErrors.aadharNumber}
                  helperText={formErrors.aadharNumber}
                  placeholder={t('personalDetails.fields.aadharPlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.phoneNumber')}
                  value={formData.phoneNumber}
                  disabled
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  placeholder={t('personalDetails.fields.phonePlaceholder')}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, color: '#1A1A1A', fontWeight: 600 }}>
                  {t('personalDetails.locationTitle')}
                </Typography>
              </Grid>

              {/* State */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('personalDetails.fields.state')}
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  helperText={formErrors.state}
                  size="small"
                  sx={modernFieldStyle}
                />
              </Grid>

              {/* District */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  fullWidth
                  options={districts}
                  getOptionLabel={(option) => option.Placename || ''}
                  loading={loadingDistricts}
                  value={districts.find(d => d.Placename === formData.district) || null}
                  onChange={(event, newValue) => {
                    handleInputChange('district', newValue ? newValue.Placename : '');
                    setSelectedDistrictId(newValue ? newValue.PlaceId : null);
                  }}
                  isOptionEqualToValue={(option, value) => option.PlaceId === value.PlaceId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('personalDetails.fields.district')}
                      error={!!formErrors.district}
                      size="small"
                      sx={modernFieldStyle}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDistricts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Taluk */}
              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 6 }}>
                <Autocomplete
                  fullWidth
                  options={taluks}
                  getOptionLabel={(option) => option.Placename || ''}
                  loading={loadingTaluks}
                  disabled={!selectedDistrictId}
                  value={taluks.find(t => t.Placename === formData.taluk) || null}
                  onChange={(event, newValue) => {
                    handleInputChange('taluk', newValue ? newValue.Placename : '');
                    setSelectedTalukId(newValue ? newValue.PlaceId : null);
                  }}
                  isOptionEqualToValue={(option, value) => option.PlaceId === value.PlaceId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('personalDetails.fields.taluk')}
                      error={!!formErrors.taluk}
                      size="small"
                      sx={modernFieldStyle}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingTaluks ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Hobli */}
              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 6 }}>
                <Autocomplete
                  fullWidth
                  options={hoblis}
                  getOptionLabel={(option) => option.Placename || ''}
                  loading={loadingHoblis}
                  disabled={!selectedTalukId}
                  value={hoblis.find(h => h.Placename === formData.hobli) || null}
                  onChange={(event, newValue) => {
                    handleInputChange('hobli', newValue ? newValue.Placename : '');
                    setSelectedHobliId(newValue ? newValue.PlaceId : null);
                  }}
                  isOptionEqualToValue={(option, value) => option.PlaceId === value.PlaceId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('personalDetails.fields.hobli')}
                      error={!!formErrors.hobli}
                      size="small"
                      sx={modernFieldStyle}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingHoblis ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Village */}
              <Grid size={{ xs: 12, md: (formData.role === 'farmer' || formData.role === 'fpo') ? 4 : 6 }}>
                <Autocomplete
                  fullWidth
                  options={villages}
                  getOptionLabel={(option) => option.Placename || ''}
                  loading={loadingVillages}
                  disabled={!selectedHobliId}
                  value={villages.find(v => v.Placename === formData.village) || null}
                  onChange={(event, newValue) => {
                    handleInputChange('village', newValue ? newValue.Placename : '');
                    setSelectedVillageId(newValue ? newValue.PlaceId : null);
                  }}
                  isOptionEqualToValue={(option, value) => option.PlaceId === value.PlaceId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('personalDetails.fields.village')}
                      error={!!formErrors.village}
                      size="small"
                      sx={modernFieldStyle}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingVillages ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  {t('personalDetails.aadharWarning')}
                </Alert>
              </Grid>

            </Grid>
          </motion.div>
        );

      case t('stepper.farmingProfile'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Sprout size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('farmingProfile.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1, md: 1.5 }}>
              {/* Dynamic Questions from API */}
              {loadingQuestions ? (
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={30} sx={{ color: '#D4A747' }} />
                    <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                      Loading questions...
                    </Typography>
                  </Box>
                </Grid>
              ) : questions.length > 0 ? (
                <>

                  {questions.map((question, index) => {
                    console.log('Rendering question:', question);
                    return (
                    <Grid size={{ xs: 12 }} key={question._id}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#1A1A1A', fontWeight: 600 }}>
                          {index + 1}. {question.questionName}
                          {question.required && <span style={{ color: '#f44336' }}> *</span>}
                        </Typography>

                        {question.choiceType === 'text' && (
                          <TextField
                            fullWidth
                            required={question.required}
                            value={questionResponses[question._id] || ''}
                            onChange={(e) => setQuestionResponses(prev => ({
                              ...prev,
                              [question._id]: e.target.value
                            }))}
                            error={!!formErrors[`question_${question._id}`]}
                            helperText={formErrors[`question_${question._id}`]}
                            size="small"
                            sx={modernFieldStyle}
                          />
                        )}

                        {question.choiceType === 'textarea' && (
                          <TextField
                            fullWidth
                            required={question.required}
                            multiline
                            rows={3}
                            value={questionResponses[question._id] || ''}
                            onChange={(e) => setQuestionResponses(prev => ({
                              ...prev,
                              [question._id]: e.target.value
                            }))}
                            error={!!formErrors[`question_${question._id}`]}
                            helperText={formErrors[`question_${question._id}`]}
                            size="small"
                            sx={modernFieldStyle}
                          />
                        )}

                        {question.choiceType === 'number' && (
                          <TextField
                            fullWidth
                            required={question.required}
                            type="number"
                            value={questionResponses[question._id] || ''}
                            onChange={(e) => setQuestionResponses(prev => ({
                              ...prev,
                              [question._id]: e.target.value
                            }))}
                            error={!!formErrors[`question_${question._id}`]}
                            helperText={formErrors[`question_${question._id}`]}
                            size="small"
                            sx={modernFieldStyle}
                          />
                        )}

                        {question.choiceType === 'single' && question.options && (
                          <RadioGroup
                            row
                            value={questionResponses[question._id] || ''}
                            onChange={(e) => setQuestionResponses(prev => ({
                              ...prev,
                              [question._id]: e.target.value
                            }))}
                          >
                            {question.options.map((option, optIndex) => (
                              <FormControlLabel
                                key={optIndex}
                                value={option.optionName || option}
                                control={<Radio sx={{ color: '#D4A747', '&.Mui-checked': { color: '#D4A747' } }} />}
                                label={option.optionName || option}
                                sx={{ '& .MuiFormControlLabel-label': { color: '#1A1A1A' } }}
                              />
                            ))}
                          </RadioGroup>
                        )}

                        {question.choiceType === 'multiple' && question.options && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {question.options.map((option, optIndex) => {
                              const optionValue = option.optionName || option;
                              const isSelected = (questionResponses[question._id] || []).includes(optionValue);
                              return (
                                <Chip
                                  key={optIndex}
                                  label={optionValue}
                                  onClick={() => {
                                    setQuestionResponses(prev => {
                                      const current = prev[question._id] || [];
                                      if (isSelected) {
                                        return { ...prev, [question._id]: current.filter(o => o !== optionValue) };
                                      } else {
                                        return { ...prev, [question._id]: [...current, optionValue] };
                                      }
                                    });
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    bgcolor: isSelected ? 'rgba(212, 167, 71, 0.2)' : 'rgba(212, 167, 71, 0.05)',
                                    border: isSelected ? '2px solid #D4A747' : '1px solid rgba(212, 167, 71, 0.3)',
                                    color: isSelected ? '#B8901C' : '#666',
                                    fontWeight: isSelected ? 600 : 400,
                                    '&:hover': {
                                      bgcolor: 'rgba(212, 167, 71, 0.15)',
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  )})}
                </>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    No questions available for this category.
                  </Alert>
                </Grid>
              )}
            </Grid>
          </motion.div>
        );

        case t('stepper.documentUpload'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Upload size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('documentUpload.title')}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
              {t('documentUpload.subtitle')}
            </Typography>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* File Upload Area */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    border: '2px dashed rgba(212, 167, 71, 0.4)',
                    borderRadius: '12px',
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'rgba(212, 167, 71, 0.02)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#D4A747',
                      backgroundColor: 'rgba(212, 167, 71, 0.06)',
                    }
                  }}
                  onClick={() => document.getElementById('supporting-documents-upload').click()}
                >
                  <input
                    id="supporting-documents-upload"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.mp4,.mov,.avi,.mkv,.webm"
                    onChange={handleSupportingDocumentUpload}
                    style={{ display: 'none' }}
                  />
                  <Upload size={48} style={{ color: '#D4A747', marginBottom: '16px' }} />
                  <Typography variant="h6" sx={{ color: '#1A1A1A', fontWeight: 600, mb: 1 }}>
                    {t('documentUpload.uploadTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    {t('documentUpload.clickToUpload')}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center',
                    mt: 2
                  }}>
                    <Chip label="PDF" size="small" sx={{ bgcolor: 'rgba(212, 167, 71, 0.1)', color: '#B8901C' }} />
                    <Chip label="Word" size="small" sx={{ bgcolor: 'rgba(212, 167, 71, 0.1)', color: '#B8901C' }} />
                    <Chip label="Images" size="small" sx={{ bgcolor: 'rgba(212, 167, 71, 0.1)', color: '#B8901C' }} />
                    <Chip label="Videos" size="small" sx={{ bgcolor: 'rgba(212, 167, 71, 0.1)', color: '#B8901C' }} />
                  </Box>
                </Box>
              </Grid>

              {/* Accepted Formats Info */}
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('documentUpload.acceptedFormats.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    • <strong>{t('documentUpload.acceptedFormats.documents')}:</strong> PDF, DOC, DOCX<br />
                    • <strong>{t('documentUpload.acceptedFormats.images')}:</strong> JPG, JPEG, PNG, GIF, BMP, WebP<br />
                    • <strong>{t('documentUpload.acceptedFormats.videos')}:</strong> MP4, MOV, AVI, MKV, WebM
                  </Typography>
                </Alert>
              </Grid>

              {/* Display Uploaded Files */}
              {formData.supportingDocuments && formData.supportingDocuments.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileText size={20} style={{ color: '#D4A747' }} />
                    {formData.supportingDocuments.length} {formData.supportingDocuments.length > 1 ? t('documentUpload.filesUploaded') : t('documentUpload.fileUploaded')}
                  </Typography>
                  <Grid container spacing={1}>
                    {formData.supportingDocuments.map((doc) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            border: '1px solid rgba(212, 167, 71, 0.2)',
                            borderRadius: '8px',
                            backgroundColor: 'rgba(212, 167, 71, 0.02)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: '#D4A747',
                              backgroundColor: 'rgba(212, 167, 71, 0.05)',
                            }
                          }}
                        >
                          <FileText size={24} style={{ color: '#D4A747', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: '#1A1A1A',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {doc.file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#999' }}>
                              {(doc.file.size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSupportingDocument(doc.id);
                            }}
                            sx={{
                              color: '#f44336',
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              }
                            }}
                          >
                            <X size={18} />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Optional Note */}
              <Grid size={{ xs: 12 }}>
                <Alert severity="success" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2">
                    <strong>{t('documentUpload.note.title')}</strong> {t('documentUpload.note.message')}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        );

        case t('stepper.achievementsProof'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Award size={24} style={{ marginRight: 8, color: '#D4A747' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('achievementsProof.title')}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* Loop through each criterion */}
              {formData.criteriaResponses && formData.criteriaResponses.length > 0 ? (
                formData.criteriaResponses.map((response, criterionIndex) => (
                  <React.Fragment key={criterionIndex}>
                    {/* Criterion Header */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#B8901C', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Award size={18} />
                        {t('achievementsProof.criterionLabel')} {criterionIndex + 1}: {response.criterionName}
                      </Typography>
                    </Grid>

                    {/* Achievement Description for this criterion */}
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={3}
                        label={t('achievementsProof.fields.achievementDescription')}
                        value={response.achievementDescription}
                        onChange={(e) => handleCriterionAchievementChange(criterionIndex, e.target.value)}
                        error={!!formErrors[`criterionAchievement_${criterionIndex}`]}
                        helperText={
                          formErrors[`criterionAchievement_${criterionIndex}`] ||
                          `${response.achievementDescription.length}/50 ${t('achievementsProof.achievementDescription.characterCount')}`
                        }
                        placeholder={t('achievementsProof.achievementDescription.placeholder')}
                        sx={modernFieldStyle}
                      />
                    </Grid>

                    {/* File Upload for this criterion */}
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          border: '2px dashed rgba(212, 167, 71, 0.3)',
                          borderRadius: '8px',
                          p: 1.5,
                          textAlign: 'center',
                          backgroundColor: 'rgba(212, 167, 71, 0.02)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#D4A747',
                            backgroundColor: 'rgba(212, 167, 71, 0.05)',
                          }
                        }}
                        onClick={() => document.getElementById(`file-upload-criterion-${criterionIndex}`).click()}
                      >
                        <input
                          id={`file-upload-criterion-${criterionIndex}`}
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleCriterionFileUpload(criterionIndex, e)}
                          style={{ display: 'none' }}
                        />
                        <Upload size={24} style={{ color: '#D4A747', marginBottom: '4px' }} />
                        <Typography variant="body2" sx={{ color: '#1A1A1A', fontWeight: 600 }}>
                          {t('achievementsProof.documents.uploadTitle')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                          {t('achievementsProof.documents.uploadHelperText')}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Display Uploaded Files for this criterion */}
                    {response.documents && response.documents.length > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, color: '#666', display: 'block' }}>
                          {response.documents.length} {response.documents.length > 1 ? t('achievementsProof.documents.documents') : t('achievementsProof.documents.document')} {t('achievementsProof.documents.documentsUploaded')}
                        </Typography>
                        <Grid container spacing={1}>
                          {response.documents.map((doc) => (
                            <Grid size={{ xs: 12 }} key={doc.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid rgba(212, 167, 71, 0.2)', borderRadius: '6px', backgroundColor: 'rgba(212, 167, 71, 0.02)' }}>
                                <FileText size={16} style={{ color: '#D4A747', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', color: '#1A1A1A' }}>
                                  {doc.file.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem' }}>
                                  {(doc.file.size / 1024).toFixed(1)} {t('achievementsProof.documents.fileSizeUnit')}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => handleCriterionRemoveDocument(criterionIndex, doc.id)}
                                  sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                    color: '#f44336',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                    }
                                  }}
                                >
                                  <X size={16} />
                                </Button>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    )}

                    {/* No Documents Reason for this criterion */}
                    {(!response.documents || response.documents.length === 0) && (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={2}
                          label={t('achievementsProof.documents.noDocumentsReason.label')}
                          value={response.noDocumentsReason}
                          onChange={(e) => handleCriterionNoDocReasonChange(criterionIndex, e.target.value)}
                          error={!!formErrors[`criterionNoDocReason_${criterionIndex}`]}
                          helperText={formErrors[`criterionNoDocReason_${criterionIndex}`] || t('achievementsProof.documents.noDocumentsReason.helperText')}
                          placeholder={t('achievementsProof.documents.noDocumentsReason.placeholder')}
                          size="small"
                          sx={modernFieldStyle}
                        />
                      </Grid>
                    )}

                    {/* Divider between criteria */}
                    {criterionIndex < formData.criteriaResponses.length - 1 && (
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    {t('achievementsProof.noCategoryMessage')}
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <strong>{t('achievementsProof.noteTitle')}</strong> {t('achievementsProof.noteMessage')}
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        );

      case t('stepper.reviewSubmit'):
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                textAlign: 'center',
                mb: 2,
                pb: 1.5,
                borderBottom: '2px solid rgba(212, 167, 71, 0.1)'
              }}
            >
              <CheckCircle size={28} style={{ color: '#D4A747', marginBottom: '8px' }} />
              <Typography
                variant="h6"
                sx={{
                  color: '#1A1A1A',
                  fontWeight: 700,
                  mb: 0.5
                }}
              >
                {t('reviewSubmit.title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '0.85rem'
                }}
              >
                {t('reviewSubmit.verificationMessage')}
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {/* Region & Category */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <MapPin size={18} />
                      Role & Category
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(0)}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: '4px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Role
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {roles.find(r => r.value === formData.role)?.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Award Category
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {categories.find(c => c._id === formData.category)?.awardName || formData.category}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Personal Details */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px solid rgba(212, 167, 71, 0.15)',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography
                      sx={{
                        color: '#B8901C',
                        fontSize: '1rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <User size={18} />
                      {t('personalDetails.sectionTitle')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep((formData.role === 'farmer' || formData.role === 'fpo') ? 1 : 2)}
                      startIcon={<Edit size={14} />}
                      sx={{
                        color: '#D4A747',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: '4px',
                        border: '1px solid rgba(212, 167, 71, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                  <Grid container spacing={0.5}>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Full Name
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.fullName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Aadhar Number
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.aadharNumber || '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Phone
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.phoneNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 0.75 }} />
                  <Typography sx={{ fontWeight: 700, color: '#B8901C', mb: 0.5, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MapPin size={18} />
                    Location
                  </Typography>
                  <Grid container spacing={0.5}>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        State
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.state}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        District
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.district}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Taluk
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.taluk}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Hobli
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.hobli}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.25 }}>
                        Village
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 500, lineHeight: 1.2 }}>
                        {formData.village}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Uploaded Documents - Only for Farmers/FPO */}
              {(formData.role === 'farmer' || formData.role === 'fpo') && (
                <Grid size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: '8px',
                      border: '1px solid rgba(212, 167, 71, 0.15)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(212, 167, 71, 0.02) 100%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography
                        sx={{
                          color: '#B8901C',
                          fontSize: '1rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Upload size={18} />
                        {t('documentUpload.title')}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setActiveStep(3)}
                        startIcon={<Edit size={14} />}
                        sx={{
                          color: '#D4A747',
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          minWidth: 'auto',
                          borderRadius: '4px',
                          border: '1px solid rgba(212, 167, 71, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          }
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                    {formData.supportingDocuments && formData.supportingDocuments.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {formData.supportingDocuments.map((doc) => (
                          <Chip
                            key={doc.id}
                            icon={<FileText size={14} />}
                            label={doc.file.name}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(212, 167, 71, 0.1)',
                              color: '#B8901C',
                              '& .MuiChip-icon': { color: '#D4A747' }
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>
                        {t('documentUpload.noDocumentsUploaded')}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  color: 'white',
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(212, 167, 71, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(212, 167, 71, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(212, 167, 71, 0.3)',
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    {t('reviewSubmit.buttons.submitting')}
                  </>
                ) : (
                  t('reviewSubmit.buttons.submit')
                )}
              </Button>
            </Box>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <Box sx={{ backgroundColor: '#FFFFFF' }}>
        <Box
          sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            overflow: 'hidden',
            backgroundImage: `url("https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1920&h=1080&fit=crop")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5) 0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <CheckCircle
                size={80}
                style={{ color: theme.palette.primary.main, marginBottom: '24px' }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #1A1A1A 27%, #D4A747 50%, #1A1A1A 73%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('messages.successTitle')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: 700,
                  mx: 'auto',
                  color: theme.palette.secondary.main,
                }}
              >
                {t('messages.successMessage')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  opacity: 0.8,
                  color: '#CCCCCC',
                }}
              >
                {t('messages.confirmationEmail')}
              </Typography>
              <Chip
                label={`${t('messages.applicationId')}: ${applicationId}`}
                sx={{
                  bgcolor: '#D4A747',
                  color: '#1A1A1A',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  px: 2,
                  py: 3,
                  mb: 4
                }}
              />
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  href="/nominations"
                  sx={{
                    borderColor: '#D4A747',
                    color: '#D4A747',
                    mr: 2,
                    py: 1.5,
                    px: 4,
                    borderRadius: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#B8901C',
                      bgcolor: 'rgba(212, 167, 71, 0.1)',
                    },
                  }}
                >
                  {t('messages.viewAllCategories')}
                </Button>
                <Button
                  variant="contained"
                  href="/"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    py: 1.5,
                    px: 5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(212, 167, 71, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(212, 167, 71, 0.4)'
                    }
                  }}
                >
                  {t('messages.backToHome')}
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', pt: { xs: 10, md: 11 } }}>
      {/* Form Section */}
      <Box sx={{ py: { xs: 1, md: 2 }, bgcolor: 'white' }}>
        <Container
          maxWidth="lg"
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            maxWidth: { xs: '100%', sm: '100%', md: 'lg' }
          }}
        >
          {/* Stepper */}
          <Paper elevation={0} sx={{ p: { xs: 1, sm: 1.5, md: 2 }, mb: { xs: 1.5, md: 2 }, border: '1px solid rgba(212, 167, 71, 0.15)', overflow: 'hidden', position: 'relative' }}>
            {/* Left Arrow - Only visible on mobile when scrollable */}
            {showLeftArrow && (
              <IconButton
                onClick={() => scrollStepper('left')}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(212, 167, 71, 0.1)',
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronLeft size={20} style={{ color: '#D4A747' }} />
              </IconButton>
            )}

            {/* Right Arrow - Only visible on mobile when scrollable */}
            {showRightArrow && (
              <IconButton
                onClick={() => scrollStepper('right')}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(212, 167, 71, 0.1)',
                  },
                  width: 32,
                  height: 32,
                }}
              >
                <ChevronRight size={20} style={{ color: '#D4A747' }} />
              </IconButton>
            )}

            <Box
              ref={stepperRef}
              sx={{
                overflowX: { xs: 'auto', md: 'visible' },
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                px: { xs: showLeftArrow || showRightArrow ? 5 : 0, md: 0 },
              }}
            >
              <Stepper activeStep={activeStep} connector={<CustomStepConnector />} sx={{ minWidth: 'max-content' }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          fontWeight: 500,
                        },
                        '& .MuiStepLabel-label.Mui-active': {
                          color: '#D4A747',
                          fontWeight: 600,
                        },
                        '& .MuiStepLabel-label.Mui-completed': {
                          color: '#B8901C',
                          fontWeight: 600,
                        },
                        '& .MuiStepIcon-root': {
                          color: 'rgba(212, 167, 71, 0.2)',
                        },
                        '& .MuiStepIcon-root.Mui-active': {
                          color: '#D4A747',
                        },
                        '& .MuiStepIcon-root.Mui-completed': {
                          color: '#B8901C',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Paper>

          {/* Form Content */}
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, border: '1px solid rgba(212, 167, 71, 0.15)', overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              {renderStepContent(activeStep)}
            </AnimatePresence>

            {/* Validation Error Alert */}
            {showValidationError && Object.keys(formErrors).length > 0 && (
              <Alert
                severity="error"
                sx={{
                  mt: 3,
                  borderRadius: '8px',
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('validationError.title')}
                </Typography>
                <Typography variant="body2">
                  {t('validationError.message')}
                </Typography>
              </Alert>
            )}

            {/* Navigation Buttons - Show for all steps except review (last step has Submit in content) */}
            {activeStep < steps.length - 1 && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, justifyContent: 'space-between', mt: { xs: 3, md: 4 }, pt: { xs: 2, md: 3 }, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                <Button
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                  startIcon={<ArrowLeft size={18} />}
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 167, 71, 0.05)',
                    },
                    '&:disabled': {
                      color: '#ccc',
                    }
                  }}
                >
                  {t('navigation.back')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: 'white',
                    px: 4,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(212, 167, 71, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B8901C 0%, #D4A747 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(212, 167, 71, 0.4)'
                    },
                    '&:disabled': {
                      background: 'rgba(212, 167, 71, 0.3)',
                    }
                  }}
                >
                  {t('navigation.next')}
                </Button>
              </Box>
            )}

            {/* Back button only for review step - Submit is shown in step content */}
            {activeStep === steps.length - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4, pt: 3, borderTop: '1px solid rgba(212, 167, 71, 0.1)' }}>
                <Button
                  onClick={handleBack}
                  disabled={loading}
                  startIcon={<ArrowLeft size={18} />}
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 167, 71, 0.05)',
                    },
                    '&:disabled': {
                      color: '#ccc',
                    }
                  }}
                >
                  {t('navigation.back')}
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default AwardsApplication;
